import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import moment from 'moment';
import { fromEvent, Subscription } from 'rxjs';
import { map, debounceTime, takeWhile } from 'rxjs/operators';

export const MY_FORMATS = {
  parse: {
    dateInput: ['L', 'LL', 'DD/MM/YYYY', 'MM/DD/YYYY'],
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'reusable-date-input',
  templateUrl: './date-input.component.html',
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class ReuseableDateComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('input', { read: ElementRef }) inputField: ElementRef<HTMLInputElement>;
  @ViewChild('dp3') dp3: MatDatepicker<any>;

  @Input() title = '';
  @Input() hasMaxDate = false;
  @Input() controlName = 'effective_date';
  @Input() maxDate = '';
  @Input() value: any = new Date();
  @Output() dateChangeEmitter = new EventEmitter();
  @Output() changeEmitter = new EventEmitter();

  @Input() ngClassStyle;
  @Input() classStyle;
  @Input() appearance;
  @Input() dataCy;
  @Input() hasClassTruncate = false;
  @Input() floatLabel = 'view';
  @Input() isRequired = false;
  @Input() labelClass = '';
  @Input() form;
  @Input() hasError = false;
  @Input() error = '';
  @Input() min;
  @Input() prefixIcon = false;
  @Input() disabled = false;
  @Input() showLabel = true;
  @Input() placeholder = 'MM/DD/YYYY';
  initialValue;
  subscription = new Subscription();
  isComponentActive = true;
  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initialValue = this.value;
    this.setInitialValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value) {
      this.setInitialValue();
    }
  }
  ngAfterViewInit(): void {
    this.setInitialValue();
    this.subscription.add(
      fromEvent(this.inputField.nativeElement, 'input')
        .pipe(
          map((event: Event) => (event.target as HTMLInputElement).value),
          debounceTime(300), // Wait for 300ms pause in events
          takeWhile(() => this.isComponentActive)
        )
        .subscribe((value) => {
          const formattedValue = this.formatDate(value);
          this.inputField.nativeElement.value = formattedValue;
          if (value.length >= 2 && this.dp3.opened) this.dp3.close();
        })
    );

    this.subscription.add(
      fromEvent(this.inputField.nativeElement, 'click')
        .pipe(
          map((event: Event) => (event.target as HTMLInputElement).value),
          debounceTime(100), // Wait for 300ms pause in events
          takeWhile(() => this.isComponentActive)
        )
        .subscribe(() => {
          this.dp3.open();
          this.inputField.nativeElement.focus();
          // if (value.length >= 2 && this.dp3.opened) this.dp3.close();
        })
    );
  }
  ngOnDestroy(): void {
    this.isComponentActive = false;
    this.subscription?.unsubscribe();
  }

  private setInitialValue() {
    if (this.value) {
      // List of supported formats, including auto-detection for ISO strings
      const formats = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', moment.ISO_8601];

      // Try to parse the date using the provided formats
      const momentDate = moment(this.value, formats, true);

      if (momentDate.isValid()) {
        // Format the date to 'MM/DD/YYYY' and convert to a JavaScript Date object
        const formattedDate = momentDate.format('MM/DD/YYYY');
        const jsDate = moment(formattedDate, 'MM/DD/YYYY').toDate();

        // Assign the parsed JavaScript date back to `this.value`
        this.value = jsDate;

        // Trigger change detection
        this.cd.detectChanges();
      } else {
        const jsDate = moment(new Date(), 'MM/DD/YYYY').toDate();
        this.value = jsDate;
        this.cd.detectChanges();
      }
    }
  }

  restrictToNumbers(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault(); // Block non-numeric characters
    }
  }
  handleDateChange(event) {
    const { target } = event;
    const { value } = target;

    // const formats = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'];
    // const formatDateType = moment(value, formats, true).format('MMMM D, YYYY');
    let formatDate = moment(value, 'MMMM D, YYYY').format('YYYY-MM-DD');

    if (!!this.hasMaxDate) {
      if (moment(value, 'MM/DD/YYYY').isAfter(this.maxDate)) {
        formatDate = moment().format('YYYY-MM-DD');
        this.value = !!this.initialValue ? moment(this.initialValue, 'MM/DD/YYYY') : moment().toDate();
      }
    }
    this.dateChangeEmitter.emit(formatDate);
  }
  setDateChange(value) {
    // const momentDate = moment(value, 'MM/DD/YYYY'); // Parse with Moment.js
    // const jsDate = momentDate.toDate();
    const formats = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'];
    const formatDateType = moment(value, formats, true).format('MM/DD/YYYY');
    let formatDate = moment(value, 'MM/DD/YYYY').format('YYYY-MM-DD');

    if (!!this.hasMaxDate) {
      if (moment(value, 'MM/DD/YYYY').isAfter(this.maxDate)) {
        formatDate = moment().format('YYYY-MM-DD');
        this.value = !!this.initialValue ? moment(this.initialValue, 'MM/DD/YYYY') : moment().toDate();
      }
    }
    this.changeEmitter.emit(formatDateType);
  }
  formatDate(value: string): string {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as MM/DD/YYYY
    let formatted = digits.slice(0, 2); // MM
    if (digits.length >= 2) {
      formatted += '/' + digits.slice(2, 4); // DD
    }
    if (digits.length >= 4) {
      formatted += '/' + digits.slice(4, 8); // YYYY
    }
    return formatted;
  }
}
